/** @jsx jsx */

import { Fragment } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { reverse, sortBy } from 'lodash';

import {
  Colors,
  Icon,
  SkeletonLoader,
  SkeletonText,
  Spacer,
  Surface,
  Text,
  TooltipWrapper,
  Typography,
} from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import ExtendedIcon from 'app/developer-v3/components/ExtendedIcon';
import VersionDropdown from 'app/developer-v3/components/VersionDropdownV5';
import { sortVersionStrings } from 'app/developer-v3/utils';

import AnalyticsTable from './AnalyticsTable';

import { VerbAnalytics, UsageType } from './types';
import type { SemVer } from 'app/developer-v3/types/version';

const Styles = {
  root: type => css`
    display: flex;
    flex-direction: column;
    background-color: ${type === UsageType.action
      ? Colors.PrimeWhite
      : Colors.GrayWarm2};
    border-top: 1px solid ${Colors.GrayWarm4};
    padding: 20px;
    & div[class$='Input-Input--root'] {
      background-color: ${type === UsageType.action
        ? Colors.PrimeWhite
        : Colors.GrayWarm2} !important;
      ${Typography.Body2};
    }
    & div[class$='FloatingBox--Styles'] {
      background-color: ${type === UsageType.action
        ? Colors.PrimeWhite
        : Colors.GrayWarm2} !important;
      ${Typography.Body2};
    }

    // Change loader color to be visible on the
    // neutral200 background of the trigger section
    &&&&&& [class*='SkeletonBlock'] {
      background: ${Colors.GrayWarm3};
    }
  `,
  header: css`
    display: flex;
    justify-content: space-between;
  `,
  title: css`
    display: flex;
    align-items: center;
  `,
  tooltipContent: css`
    min-width: 200px;
  `,
  infoText: css`
    display: flex;
    justify-content: center;
  `,
  updatedTime: css`
    font-size: 90%;
    text-align: right;
    margin-top: 15px;
    font-style: italic;
    color: ${Colors.GrayWarm4};
  `,
};

export type Props = {
  analytics: VerbAnalytics[];
  ariaLabel: string;
  timestamp: number;
  isLoading: boolean;
  onChangeVersion: (value: SemVer) => void;
  title: string;
  type: UsageType.action | UsageType.trigger;
  version: SemVer;
  versions: SemVer[];
  dataTestId?: string;
};

function Usage(props: Props) {
  const {
    analytics,
    ariaLabel,
    timestamp,
    isLoading,
    onChangeVersion,
    title,
    type,
    version,
    versions = [],
    dataTestId,
  } = props;

  const tableData = reverse(
    sortBy(isLoading ? [] : analytics, ['zapsTotal', 'zapsLive', 'zapsPaused'])
  );

  const tooltipContent = (
    <div css={Styles.tooltipContent} data-testid={dataTestId}>
      <Text color="PrimeWhite" tag="p" type="Body1">
        Numbers of current live Zaps (Zaps that are turned on), paused Zaps
        (Zaps that are turned off), and total Zaps for each{' '}
        {type === UsageType.action ? 'action' : 'trigger'}.
      </Text>
    </div>
  );

  return (
    <figure aria-label={ariaLabel} css={Styles.root(type)}>
      <div css={Styles.header}>
        <div css={Styles.title}>
          <ExtendedIcon
            name={type === UsageType.action ? 'action' : 'trigger'}
            size={24}
          />
          <Text margin="6px" type="ParagraphHeader3">
            {title}
          </Text>
          <TooltipWrapper
            allowMultilineTooltip={true}
            content={tooltipContent}
            isBlock={true}
          >
            {() => (
              <div style={{ display: 'flex' }}>
                <Icon color="GrayWarm6" name="alertInfoFill" size="20px" />
              </div>
            )}
          </TooltipWrapper>
        </div>
        {versions.length && (
          <div css={{ minWidth: '20ch' }}>
            <VersionDropdown
              onChange={onChangeVersion}
              version={version}
              versions={sortVersionStrings(versions)}
            />
          </div>
        )}
      </div>
      <Spacer height={3} />
      <AnalyticsTable
        items={tableData}
        type={type === UsageType.action ? 'Action' : 'Trigger'}
      />
      {isLoading ? (
        <SkeletonLoader>
          <Spacer height={20} />
          <SkeletonText lines={3} size="heading" />
        </SkeletonLoader>
      ) : (
        !analytics.length &&
        (timestamp ? (
          <Fragment>
            <Spacer height={20} />
            <Surface>
              <div css={Styles.infoText}>
                <Text margin="20px" textAlign="center" type="Body1">
                  You have no{' '}
                  {type === UsageType.action ? 'actions' : 'triggers'} or the
                  data is not refreshed yet
                </Text>
              </div>
            </Surface>
          </Fragment>
        ) : (
          <Fragment>
            <Spacer height={20} />
            <Surface>
              <div css={Styles.infoText}>
                <Text margin="20px" textAlign="center" type="Body1">
                  Data is not available yet. Come back later.
                </Text>
              </div>
            </Surface>
          </Fragment>
        ))
      )}
      {timestamp && (
        <div css={Styles.updatedTime}>
          <Text margin="0px 5px 0px 10px" tag="span">
            {`Updated ${formatDistanceToNowStrict(timestamp * 1000, {
              addSuffix: true,
            })}`}
          </Text>
        </div>
      )}
    </figure>
  );
}

export default Usage;
